import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import Icon from '@mdi/react';
import { getDataFromKey } from '../../utils';
import { useMemo, useState } from 'react';
import { Pagination } from './Pagination';
import { SearchIcon } from 'lucide-react';
import { mdiLoading } from '@mdi/js';
import { MAIN_BG_COLOR, MAIN_TXT_COLOR } from './common';
import { useTranslate } from 'sbx-crm-menu-ui';
import { motion } from 'framer-motion';
import { Input } from '../ui/input';

export interface ActionButton<DataType> {
    label: string;
    icon: string | JSX.Element;
    className?: string;
    onClick: (data: DataType) => void;
    visible?: (data: DataType) => boolean;
    disabled?: (data: DataType) => boolean;
    loading?: (data: DataType) => boolean;
}

export enum KeyHeader {
    KEY,
    VALUE
}

interface MainHeader {
    label: string;
    visible?: boolean;
    sortable?: boolean;
    className?: string;
}


interface Header1<DataType> extends MainHeader {
    type: KeyHeader.KEY;
    key: keyof DataType | string;
}

interface Header2<DataType> extends MainHeader {
    type: KeyHeader.VALUE;
    toValue: (data: DataType) => JSX.Element | string;
}

export type Header<DataType> = Header1<DataType> | Header2<DataType>;

interface Props<DataType> {
    items: DataType[];
    actions?: ActionButton<DataType>[];
    pagination?: boolean;
    search?: boolean;
    count?: number;
    loading?: boolean;
    rowNavigation?: (data: DataType) => void;
    header: Header<DataType>[],
    topActions?: JSX.Element;
    filterActions?: JSX.Element;
    rowClassName?: string;
    tableClassName?: string
    actionsLabelClassName?: string
    actionsClassName?: string
    searchClassName?: string
    buttonPaginationClassName?: string
    pagePaginationClassName?:(page:number, num: number) => string
}

const ITEMS_PER_PAGE = Math.floor((window.innerHeight) / 76);

export default function TableComponent<DataType>(
    {
        items,
        header,
        actions,
        pagination,
        search,
        count = ITEMS_PER_PAGE,
        loading,
        rowNavigation,
        topActions, filterActions, pagePaginationClassName,
        rowClassName = '', searchClassName = '', buttonPaginationClassName = '',
        tableClassName = '', actionsLabelClassName = '', actionsClassName =  ''
    }: Props<DataType>) {

    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const totalPages = Math.ceil(items.length / count);
    const { t } = useTranslate('common');


    function getValueFromHeadAndItem(item: DataType, head: Header<DataType>) {
        return head.type === KeyHeader.KEY ? getDataFromKey(item, head.key as string) : head.toValue(item);
    }

    //filter with search input 
    const itemsFiltered = useMemo(() => {
        return items.filter((item) => {
            if (!searchValue) return true;
            return header.some((head) => {
                const data = getValueFromHeadAndItem(item, head);
                return String(data).toLowerCase().includes(searchValue.toLowerCase());
            });
        });
    }, [searchValue, items]);

    // Pagination per page and count
    const itemsPerPagination = useMemo(() => {
        return itemsFiltered.slice((page - 1) * count, page * count);
    }, [page, itemsFiltered]);


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Icon size={2} path={mdiLoading} className="text-primary" spin />
            </div>
        );
    }

    return (
        <div>
            {search && (
                <div className="flex gap-2 md:gap-0 flex-col md:flex-row md:justify-between items-center">
                    <div className="relative md:w-1/3 w-full ">
                        <Input
                            type="text"
                            className={searchClassName || 'px-4 py-1 h-[38px] border-2 w-full border-solid border-gray-200 rounded outline-none'}
                            onChange={(e) => setSearchValue(e.target.value)}
                            value={searchValue}
                            placeholder={t('search') + '...'}
                        />
                        <SearchIcon className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 right-2" />
                    </div>
                    {topActions}
                </div>
            )}

            {
                filterActions ? <div className="mb-4">
                    {filterActions}
                </div> : null
            }

            <Table className={`${tableClassName || "rounded border border-gray-200"} `}>
                <TableHeader>
                    <TableRow>
                        {header.map((head) => (
                            <TableHead className={head.className} key={head.label}>{head.label}</TableHead>
                        ))}
                        {actions && <TableHead className={`text-right ${actionsLabelClassName}`}>{t("actions")}</TableHead>}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {itemsPerPagination.map((item, index) => (
                        <motion.tr
                            key={`motion_${String((item as any)['_KEY']).concat(index.toString())}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => rowNavigation?.(item)}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className={`${(rowNavigation && !rowClassName) ? 'hover:bg-gray-100 cursor-pointer transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted' : rowClassName} border-b`}
                        >
                            {/*<TableRow onClick={() => rowNavigation?.(item)}*/}
                            {/*          title={rowNavigation ? 'Click to view details' : ''}*/}
                            {/*          className={rowNavigation ? 'hover:bg-gray-100 cursor-pointer' : ''}*/}
                            {/*          key={String((item as any)['_KEY']).concat(index.toString())}>*/}
                            {header.map((head, index) => (
                                <TableCell
                                    key={index}>{getValueFromHeadAndItem(item, head)}</TableCell>
                            ))}
                            {actions && (
                                <TableCell>
                                    <div className={`${actionsClassName || "flex justify-end gap-2"}`}>
                                        {actions.filter(action => action.visible ? action.visible(item) : true).map((action) => (
                                            <button
                                                key={action.label}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    action.onClick(item);
                                                }}
                                                type="button"
                                                className={action.className || `${MAIN_BG_COLOR} ${MAIN_TXT_COLOR} p-2 rounded`}
                                                title={action.label}
                                            >

                                                {typeof action.icon === 'string' ?
                                                    <Icon size={.6} path={action.icon} /> : action.icon}
                                            </button>
                                        ))}
                                    </div>
                                </TableCell>
                            )}
                            {/*</TableRow>*/}
                        </motion.tr>
                    ))}
                </TableBody>
            </Table>

            {!itemsFiltered.length && (
                <div className="flex justify-center items-center w-full h-[30vh] bg-gray-50 rounded">
                    <b className="text-gray-400">La tabla esta vacia</b>
                </div>
            )}

            {pagination && itemsFiltered.length > count && (
                <Pagination page={page} pageClassName={pagePaginationClassName} buttonClassName={buttonPaginationClassName} setPage={setPage} totalPages={totalPages} rowCount={items.length} />
            )}
        </div>
    );
};
